// Minimum and par levels (M6, `restock_level`): the products that have them, for every
// branch or one; add, change or clear them. The restock suggestion uses a branch's own
// level, else the every-branch one.

import type { RestockLevel } from '@enp/domain/api';
import { Button, EmptyState, PageHeader } from '@enp/ui';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Pencil, Plus, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { LevelDialog } from '../../../../purchasing/levels.js';
import {
  BaseQty,
  KeyHints,
  NoPermission,
  PurchasingBreadcrumb,
  TableSkeleton,
  useBranches,
  useCan,
  usePageKeys,
} from '../../../../purchasing/parts.js';
import { restockLevelsQuery } from '../../../../purchasing/queries.js';
import { ProductPickDialog } from '../../../../purchasing/supplier-parts.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';

export const Route = createFileRoute('/_app/purchasing/restock/levels')({
  component: LevelsPage,
});

type Editing = { product: RestockLevel['product']; level: RestockLevel | null };

function LevelsPage() {
  useDocumentTitle('Minimum and par levels');
  const q = useQuery(restockLevelsQuery());
  const canEdit = useCan('purchasing.order');
  const { branches } = useBranches();
  const [editing, setEditing] = useState<Editing | null>(null);
  const [picking, setPicking] = useState(false);
  const branchName = (id: string | null) =>
    id === null ? 'Every branch' : (branches.find((b) => b.id === id)?.name ?? 'One branch');
  const levels = q.data?.items ?? [];
  // n: set a level; e: change the level of the row in focus (else the first) (UI6A-18).
  const editFocused = () => {
    const rows = Array.from(
      document.querySelectorAll('table[aria-label="Minimum and par levels"] tbody tr'),
    );
    const at = rows.findIndex((row) => row.contains(document.activeElement));
    const l = levels[Math.max(0, at)];
    if (l) setEditing({ product: l.product, level: l });
  };
  usePageKeys({
    n: canEdit ? () => setPicking(true) : undefined,
    e: canEdit && levels.length > 0 ? editFocused : undefined,
  });

  return (
    <>
      <PageHeader
        eyebrow={
          <PurchasingBreadcrumb
            trail={[{ to: '/purchasing/restock', label: 'Restock' }]}
            page="Levels"
          />
        }
        title="Minimum and par levels"
        description="Par: the restock suggestion orders up to it instead of the worked-out cover. Minimum: it never aims below it. Useful for slow sellers, new products and anything with little sales history."
        actions={
          canEdit ? (
            <span className="flex items-center gap-3">
              <KeyHints
                keys={[
                  ['n', 'set a level'],
                  ...(levels.length > 0 ? ([['e', 'change']] as [string, string][]) : []),
                ]}
              />
              {/* None set yet: the empty state's "Set a level" is the one action (UI8C-6). */}
              {(!q.data || levels.length > 0) && (
                <Button
                  icon={<Plus aria-hidden />}
                  aria-keyshortcuts="n"
                  onClick={() => setPicking(true)}
                >
                  Set a level
                </Button>
              )}
            </span>
          ) : undefined
        }
      />
      {canEdit === false && (
        <div className="mb-4">
          <NoPermission>Levels are set by people who order stock. Ask the owner.</NoPermission>
        </div>
      )}
      {q.isError && !q.data ? (
        <ErrorPanel
          error={q.error}
          titleAs="h2"
          what="The levels"
          onRetry={() => void q.refetch()}
        />
      ) : !q.data ? (
        <TableSkeleton
          label="Minimum and par levels"
          rows={3}
          columns={[
            { header: 'Product' },
            { header: 'For' },
            { header: 'Minimum', align: 'right' },
            { header: 'Par', align: 'right' },
            { header: 'Changed', className: 'hidden lg:table-cell' },
            ...(canEdit ? [{ header: '', empty: true }] : []),
          ]}
        />
      ) : q.data.items.length === 0 ? (
        <EmptyState
          icon={SlidersHorizontal}
          title="No levels set"
          description="Without them the suggestion works from sales alone. Set a par for products that must always be on the shelf."
          action={
            <>
              {canEdit && (
                <Button icon={<Plus aria-hidden />} onClick={() => setPicking(true)}>
                  Set a level
                </Button>
              )}
              <Button variant="secondary" asChild>
                <Link to="/purchasing/restock">Back to the suggestion</Link>
              </Button>
            </>
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface-raised">
          <table aria-label="Minimum and par levels" className="w-full border-collapse text-body">
            <thead>
              <tr className="bg-surface-sunken text-left text-xs font-semibold uppercase tracking-wide text-muted">
                <th scope="col" className="px-3 py-2">
                  Product
                </th>
                <th scope="col" className="px-3 py-2">
                  For
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  Minimum
                </th>
                <th scope="col" className="px-3 py-2 text-right">
                  Par
                </th>
                <th scope="col" className="hidden px-3 py-2 lg:table-cell">
                  Changed
                </th>
                {canEdit && (
                  <th scope="col" className="px-3 py-2">
                    <span className="sr-only">Change</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((l) => (
                <tr key={`${l.product.id}|${l.branch_id ?? ''}`} className="border-t border-border">
                  <td className="px-3 py-2 font-medium text-fg">{l.product.name}</td>
                  <td className="px-3 py-2 text-sm text-fg">{branchName(l.branch_id)}</td>
                  <td className="px-3 py-2 text-right">
                    {l.min_qty === null ? (
                      <span className="text-sm text-muted">None</span>
                    ) : (
                      <BaseQty value={l.min_qty} unit={l.product.base_uom} />
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {l.par_qty === null ? (
                      <span className="text-sm text-muted">None</span>
                    ) : (
                      <BaseQty value={l.par_qty} unit={l.product.base_uom} />
                    )}
                  </td>
                  <td className="hidden px-3 py-2 text-sm text-muted lg:table-cell">
                    <AccountDate value={l.updated_at} withTime />
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Pencil aria-hidden />}
                        aria-label={`Change the levels of ${l.product.name} (${branchName(l.branch_id)})`}
                        onClick={() => setEditing({ product: l.product, level: l })}
                      >
                        Change
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {picking && (
        <ProductPickDialog
          title="Set a level for which product?"
          onPick={(product) => {
            setPicking(false);
            setEditing({ product, level: null });
          }}
          onClose={() => setPicking(false)}
        />
      )}
      {editing && (
        <LevelDialog
          product={editing.product}
          level={editing.level}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
